import { cn } from '@/utils/cn';

export const editorStyles = {
  wrapper: cn(
    'border rounded-lg overflow-hidden',
    'border-secondary-300 dark:border-secondary-600',
    'focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent',
    'transition-colors duration-200'
  ),
  wrapperError: cn(
    'border-danger-300 dark:border-danger-600',
    'focus-within:ring-danger-500'
  ),
  wrapperDisabled: cn(
    'opacity-50 cursor-not-allowed',
    'bg-secondary-100 dark:bg-secondary-900'
  ),
  toolbar: cn(
    'flex flex-wrap items-center gap-1 p-2',
    'border-b border-secondary-200 dark:border-secondary-700',
    'bg-secondary-50 dark:bg-secondary-800/50'
  ),
  toolbarGroup: cn(
    'flex items-center gap-0.5',
    'px-1',
    'border-r border-secondary-200 dark:border-secondary-700',
    'last:border-r-0'
  ),
  toolbarButton: cn(
    'p-1.5 rounded',
    'text-secondary-600 dark:text-secondary-400',
    'hover:bg-secondary-200 dark:hover:bg-secondary-700',
    'hover:text-secondary-900 dark:hover:text-secondary-100',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent',
    'transition-colors duration-150'
  ),
  toolbarButtonActive: cn(
    'bg-primary-100 dark:bg-primary-900/30',
    'text-primary-700 dark:text-primary-300',
    'hover:bg-primary-200 dark:hover:bg-primary-900/50'
  ),
  toolbarSelect: cn(
    'px-2 py-1 rounded text-sm',
    'bg-white dark:bg-secondary-800',
    'border border-secondary-200 dark:border-secondary-600',
    'text-secondary-700 dark:text-secondary-300',
    'focus:outline-none focus:ring-1 focus:ring-primary-500',
    'cursor-pointer'
  ),
  content: cn(
    'px-3 py-2',
    'bg-white dark:bg-secondary-800',
    'text-secondary-900 dark:text-white',
    'min-h-[150px]',
    'overflow-y-auto'
  ),
  // ProseMirror specific styles
  prose: cn(
    'prose prose-sm max-w-none dark:prose-invert',
    'prose-headings:font-semibold prose-headings:text-secondary-900 dark:prose-headings:text-white',
    'prose-p:text-secondary-700 dark:prose-p:text-secondary-300',
    'prose-p:my-2 prose-headings:my-3',
    'prose-ul:my-2 prose-ol:my-2',
    'prose-li:my-0.5',
    'prose-blockquote:border-l-primary-500 prose-blockquote:text-secondary-600 dark:prose-blockquote:text-secondary-400',
    'focus:outline-none'
  ),
  placeholder: cn(
    'text-secondary-400 dark:text-secondary-500',
    'pointer-events-none',
    'float-left h-0'
  ),
  label: cn(
    'block text-sm font-medium mb-2',
    'text-secondary-900 dark:text-secondary-100'
  ),
  required: cn('ml-1 text-danger-500'),
  helpText: cn('mt-1 text-xs text-secondary-500 dark:text-secondary-400'),
  errorText: cn('mt-1 text-xs text-danger-600 dark:text-danger-400'),
};

// CSS for ProseMirror placeholder
export const proseMirrorStyles = `
  .ProseMirror {
    outline: none;
  }

  .ProseMirror p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: rgb(148 163 184);
    pointer-events: none;
    height: 0;
  }

  .dark .ProseMirror p.is-editor-empty:first-child::before {
    color: rgb(100 116 139);
  }

  .ProseMirror:focus {
    outline: none;
  }

  .ProseMirror h2 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }

  .ProseMirror h3 {
    font-size: 1.1rem;
    font-weight: 600;
    margin-top: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .ProseMirror ul,
  .ProseMirror ol {
    padding-left: 1.5rem;
  }

  .ProseMirror blockquote {
    border-left: 3px solid rgb(0 174 199);
    padding-left: 1rem;
    margin-left: 0;
    font-style: italic;
  }

  .ProseMirror a {
    color: rgb(0 174 199);
    text-decoration: underline;
  }

  .dark .ProseMirror a {
    color: rgb(102 219 237);
  }

  /* Table styles */
  .ProseMirror table.rich-text-table,
  .ProseMirror table {
    border-collapse: collapse;
    width: 100%;
    margin: 0.75rem 0;
    overflow: hidden;
  }

  .ProseMirror table th,
  .ProseMirror table td {
    border: 1px solid rgb(203 213 225);
    padding: 0.5rem 0.75rem;
    vertical-align: top;
    min-width: 80px;
    text-align: left;
  }

  .ProseMirror table th {
    background: rgb(241 245 249);
    font-weight: 600;
  }

  .dark .ProseMirror table th,
  .dark .ProseMirror table td {
    border-color: rgb(71 85 105);
  }

  .dark .ProseMirror table th {
    background: rgb(30 41 59);
  }

  .ProseMirror table .selectedCell {
    background: rgb(219 234 254);
  }

  .dark .ProseMirror table .selectedCell {
    background: rgb(30 58 138);
  }
`;
